import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { ShieldCheck, ShieldOff, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Free QR generation via api.qrserver.com (no dep)
function qrUrl(otpauth) {
  return `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(otpauth)}&size=220x220&bgcolor=0a0a0a&color=10b981&margin=8`;
}

export default function AdminSecurityPage() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ["totp-status"],
    queryFn: async () => (await api.get("/auth/2fa/status")).data,
  });
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  useEffect(() => {
    try {
      const msg = sessionStorage.getItem("admin-2fa-setup-msg");
      if (msg) {
        toast.info(msg);
        sessionStorage.removeItem("admin-2fa-setup-msg");
      }
    } catch (e) { /* ignore */ }
  }, []);

  const setup = useMutation({
    mutationFn: async () => (await api.post("/auth/2fa/setup")).data,
    onSuccess: (d) => setSetupData(d),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const enable = useMutation({
    mutationFn: async () => (await api.post("/auth/2fa/enable", { code })).data,
    onSuccess: () => {
      setSetupData(null); setCode("");
      qc.invalidateQueries({ queryKey: ["totp-status"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("2FA activée");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const disable = useMutation({
    mutationFn: async () => (await api.post("/auth/2fa/disable", { code: disableCode })).data,
    onSuccess: () => {
      setDisableCode("");
      qc.invalidateQueries({ queryKey: ["totp-status"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("2FA désactivée");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const enabled = status.data?.enabled;

  return (
    <div className="min-h-screen">
      <section className="border-b border-white/5">
        <div className="mx-auto max-w-5xl px-6 py-12">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">Console admin</div>
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-md bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </span>
            <h1 className="font-display font-black text-3xl sm:text-4xl">Sécurité 2FA</h1>
          </div>
          <p className="mt-3 text-zinc-400 max-w-2xl">
            Ajoute un second facteur (code TOTP à 6 chiffres) exigé avant toute action
            administrateur : ajout / modification / suppression d'un mouvement, gestion des
            utilisateurs.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-10">
        <Card className="p-6 bg-card border-white/5" data-testid="totp-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">Statut</div>
              <div className={`mt-1 flex items-center gap-2 font-display font-bold text-xl ${enabled ? "text-emerald-400" : "text-zinc-400"}`}>
                {enabled ? <ShieldCheck className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
                {enabled ? "Activée" : "Désactivée"}
              </div>
            </div>
          </div>

          {!enabled && !setupData && (
            <div>
              <p className="text-sm text-zinc-400 mb-4">
                Installez une app authenticator (Google Authenticator, 1Password, Bitwarden, Authy…)
                puis démarrez la configuration.
              </p>
              <Button onClick={() => setup.mutate()} disabled={setup.isPending} className="bg-emerald-500 text-black hover:bg-emerald-300" data-testid="totp-setup-btn">
                {setup.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Configurer la 2FA"}
              </Button>
            </div>
          )}

          {!enabled && setupData && (
            <div className="grid md:grid-cols-2 gap-6 items-start" data-testid="totp-setup-panel">
              <div className="flex flex-col items-center gap-3">
                <img src={qrUrl(setupData.otpauth_url)} alt="QR code TOTP" className="w-56 h-56 rounded-lg border border-white/10 bg-black" />
                <div className="text-xs text-zinc-500">Scannez avec votre app authenticator</div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Secret (saisie manuelle)</Label>
                  <div className="mt-2 flex gap-2">
                    <Input readOnly value={setupData.secret} className="font-mono-num text-xs bg-white/[0.03] border-white/10" data-testid="totp-secret" />
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(setupData.secret); toast.success("Secret copié"); }} className="border-white/10 bg-transparent">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Code à 6 chiffres généré par l'app</Label>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="mt-2 font-mono-num text-lg tracking-[0.4em] text-center bg-white/[0.03] border-white/10"
                    data-testid="totp-enable-code"
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setSetupData(null); setCode(""); }} className="border-white/10 bg-transparent">Annuler</Button>
                  <Button onClick={() => enable.mutate()} disabled={code.length !== 6 || enable.isPending} className="bg-emerald-500 text-black hover:bg-emerald-300 flex-1" data-testid="totp-enable-btn">
                    {enable.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activer la 2FA"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {enabled && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                La 2FA est active. Après chaque connexion, vous devrez déverrouiller la console
                admin avec un code TOTP (session déverrouillée 4h).
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/15 text-rose-300" data-testid="totp-disable-btn">
                    <ShieldOff className="w-4 h-4 mr-1.5" /> Désactiver la 2FA
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Désactiver la 2FA ?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      Entrez le code TOTP courant pour confirmer.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Input
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="font-mono-num text-lg tracking-[0.4em] text-center bg-white/[0.03] border-white/10 my-2"
                    data-testid="totp-disable-code"
                  />
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => disable.mutate()} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="totp-disable-confirm">
                      Désactiver
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
